<script setup>
  import { NLayout, NLayoutContent, NLayoutHeader, NLayoutSider, useMessage, NLayoutFooter } from 'naive-ui'
  import NavigationMenu from '@/components/navigation-menu.vue'
  import FooterInfo from '@/components/footer-info.vue'
  import { useUserActivityWorker } from '@/composables/use-user-activity-worker'
  import { useAuthStore } from '@/stores/auth'
  import { generateStoreName } from '@/services/store-utils'
  import { useRoute, useRouter } from 'vue-router'
  import { log } from '@/services/log'
  import { computed, watch, ref, provide, inject } from 'vue'
  import AboutInfo from '@/components/basic/about-info.vue'
  import ResetPasswordModal from '@/components/basic/reset-password-modal.vue'
  import UploadOngoingMOdal from '@/components/basic/upload-ongoing-modal.vue'
  import RemoteCommandConfigRunModal from '@/components/basic/remote-command-config-run-modal.vue'

  const baseURL = import.meta.env.VUE_APP_API_URL
  const props = defineProps({
    layoutClass: String,
    contentClass: String,
  })
  const currentStore = ref(null)
  const currentStoreFn = ref(null)
  const router = useRouter()
  const appStoreFn = inject('appStoreFn')
  const appStore = appStoreFn()
  const navStyle = computed(() => appStore.navStyle)
  const isNavLeft = computed(() => navStyle?.value == 'left')
  const isNavTop = computed(() => navStyle?.value == 'top')

  const message = useMessage()
  const authStore = useAuthStore()
  const authSuccess = computed(() => authStore.authStatus === 'SUCCESS')
  const layoutClass = computed(() => props?.layoutClass ?? 'app-layout')
  const contentClass = computed(() => props?.contentClass ?? 'app-content')
  const { sendMessage } = useUserActivityWorker()
  const versionedName = generateStoreName()
  const userActivityWorkerObj = {
    token: authStore.token,
    type: 'start',
    url: `${baseURL}/user-activity/batch`,
    versionedName: versionedName,
  }
  sendMessage(userActivityWorkerObj)
  const route = useRoute()
  const name = computed(() => {
    const value = route?.path
    log('Router view key', value)
    return value
  })
  watch(authSuccess, (success) => {
    if (!success) {
      if (route?.meta?.requiresAuth) {
        message.warning('You must be authorized to access. Please login.')
        router.push('/login')
      }
    } else message.success('Login Sucessful.')
  })
  watch(
    () => route.path,
    () => {
      currentStoreFn.value = route.meta.storeFn || null
      if (typeof currentStoreFn.value === 'function') {
        currentStore.value = currentStoreFn.value()
        log('Updated store:', currentStore.value)
      } else {
        currentStore.value = null
        log('No store function found in route meta.')
      }
    },
    { immediate: true }
  )
  const appLayoutRef = ref(null)
  provide('appLayoutRef', appLayoutRef)
</script>
<template>
  <NLayout
    :ref="appLayoutRef"
    :class="layoutClass"
  >
    <NLayoutHeader
      v-if="isNavTop"
      class="top-nav-wrapper"
    >
      <NavigationMenu
        :is-nav-top="isNavTop"
        :vertical="false"
      />
    </NLayoutHeader>
    <NLayoutContent>
      <NLayout
        v-if="authSuccess"
        :has-sider="isNavLeft"
      >
        <NLayoutSider
          v-if="isNavLeft"
          :bordered="true"
          collapse-mode="width"
          :collapsed-width="50"
          width="15vw"
          :native-scrollbar="false"
          show-trigger="bar"
          :show-collapsed-content="false"
          @after-enter="appStore.showWelcomeTitle = true"
          @after-leave="appStore.showWelcomeTitle = false"
        >
          <NavigationMenu
            :is-nav-top="isNavTop"
            :vertical="true"
          />
        </NLayoutSider>
        <NLayoutContent :class="contentClass">
          <RouterView :key="name" />
        </NLayoutContent>
      </NLayout>
      <RouterView
        v-else
        :key="name"
      />
      <ResetPasswordModal></ResetPasswordModal>
      <UploadOngoingMOdal></UploadOngoingMOdal>
      <RemoteCommandConfigRunModal></RemoteCommandConfigRunModal>
      <AboutInfo></AboutInfo>
    </NLayoutContent>
    <NLayoutFooter class="bottom-nav-wrapper">
      <FooterInfo />
    </NLayoutFooter>
  </NLayout>
</template>
